import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { Star, User, LogOut, LayoutDashboard, Shield, Menu, X, Lock } from "lucide-react";
import LanguageToggle from "./LanguageToggle";
import CurrencyToggle from "./CurrencyToggle";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: t("nav.kundli"), path: "/kundli", key: "kundli" },
    { label: t("nav.aiChat"), path: "/chat", key: "ai-chat" },
    { label: t("match.navLabel"), path: "/matching", key: "matching" },
    { label: t("nav.consult"), path: "/consultation", key: "consult" },
    { label: t("nav.blog"), path: "/blog", key: "blog" },
    ...(user ? [{ label: t("nav.dashboard"), path: "/dashboard", key: "dashboard" }] : []),
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
    setMobileOpen(false);
  };

  return (
    <nav
      data-testid="navbar"
      style={{
        position: "fixed", top: 0, width: "100%", zIndex: 100,
        background: scrolled ? "rgba(18, 0, 33, 0.92)" : "rgba(18, 0, 33, 0.6)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        transition: "background 0.3s ease"
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "64px" }}>
          {/* Logo */}
          <button
            data-testid="nav-logo"
            onClick={() => navigate("/")}
            style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", padding: 0 }}
          >
            <img
              src="/logo-mark.png"
              alt="AstroFate"
              style={{ height: "40px", width: "40px", objectFit: "contain" }}
            />
            <span className="font-cinzel" style={{ fontSize: "1.3rem", fontWeight: "700", color: "#FFD166", textShadow: "0 0 20px rgba(255,209,102,0.4)" }}>
              AstroFate
            </span>
          </button>

          {/* Desktop links */}
          <div className="nav-desktop">
            {navLinks.map(link => (
              <button
                key={link.path}
                data-testid={`nav-link-${link.key}`}
                onClick={() => navigate(link.path)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: location.pathname === link.path ? "#FFD166" : "rgba(255,255,255,0.7)",
                  fontFamily: "'DM Sans', sans-serif", fontWeight: "500", fontSize: "0.9rem",
                  transition: "color 0.2s",
                }}
                onMouseEnter={e => e.target.style.color = "#FFD166"}
                onMouseLeave={e => e.target.style.color = location.pathname === link.path ? "#FFD166" : "rgba(255,255,255,0.7)"}
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Auth area */}
          <div className="nav-desktop">
            <CurrencyToggle />
            <LanguageToggle />
            {user ? (
              <>
                {user.role === "admin" && (
                  <button data-testid="nav-admin" onClick={() => navigate("/admin")} className="btn-ghost" style={{ padding: "8px 16px", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "6px" }}>
                    <Shield size={14} /> {t("nav.admin")}
                  </button>
                )}
                <button data-testid="nav-dashboard" onClick={() => navigate("/dashboard")} className="btn-ghost" style={{ padding: "8px 16px", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "6px" }}>
                  <LayoutDashboard size={14} /> {t("nav.dashboard")}
                </button>
                <button data-testid="nav-account-privacy" onClick={() => navigate("/account/privacy")} className="btn-ghost" style={{ padding: "8px 16px", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "6px" }}>
                  <Lock size={14} /> Privacy
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "linear-gradient(135deg, #9D4EDD, #5B1E8C)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem", fontWeight: "700" }}>
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <button data-testid="nav-logout" onClick={handleLogout} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)" }}>
                    <LogOut size={16} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <button data-testid="nav-login" onClick={() => navigate("/auth?tab=login")} className="btn-ghost" style={{ padding: "8px 20px", fontSize: "0.85rem" }}>{t("nav.login")}</button>
                <button data-testid="nav-signup" onClick={() => navigate("/auth?tab=signup")} className="btn-gold" style={{ padding: "8px 20px", fontSize: "0.85rem" }}>{t("nav.getStarted")}</button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="nav-mobile-btn" style={{ background: "none", border: "none", cursor: "pointer", color: "white" }}>
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="nav-mobile-menu" style={{ background: "rgba(18,0,33,0.98)", padding: "16px 0 24px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
            <div style={{ padding: "0 24px 12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <LanguageToggle compact />
              <CurrencyToggle compact />
            </div>
            {navLinks.map(link => (
              <button key={link.path} onClick={() => { navigate(link.path); setMobileOpen(false); }}
                style={{ display: "block", width: "100%", padding: "12px 24px", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.8)", textAlign: "left", fontFamily: "'DM Sans', sans-serif", fontSize: "1rem" }}>
                {link.label}
              </button>
            ))}
            <div style={{ padding: "12px 24px 0", display: "flex", gap: "12px" }}>
              {user ? (
                <button onClick={handleLogout} className="btn-ghost" style={{ flex: 1 }}>{t("nav.logout")}</button>
              ) : (
                <>
                  <button onClick={() => { navigate("/auth?tab=login"); setMobileOpen(false); }} className="btn-ghost" style={{ flex: 1 }}>{t("nav.login")}</button>
                  <button onClick={() => { navigate("/auth?tab=signup"); setMobileOpen(false); }} className="btn-gold" style={{ flex: 1 }}>{t("nav.signup")}</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
